import React, { useEffect, useState } from 'react';
import { Input, Button, Form, message, AutoComplete, Card, Spin } from 'antd';
import { SearchOutlined, FileSearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../baseUrl';
import AlertDialog from '../../../components/AlertDialog';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { generateAndDownloadHostelFeeBill } from './generateAndDownloadHostelFeeBill';

const PayHostelDevelopmentFee = () => {
    const [form] = Form.useForm();
    const [admissionNo, setAdmissionNo] = useState('');
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [hdfDetails, setHdfDetails] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState(0);

    // Modal states
    const [open1, setOpen1] = useState(false);
    const [open2, setOpen2] = useState(false);
    const [modalText, setModalText] = useState("");
    const [modalHeading, setModalHeading] = useState("");

    // Fetch admission number suggestions
    useEffect(() => {
        const searchAdmissionNumbers = async () => {
            if (admissionNo.length > 2) {
                try {
                    const response = await axios.get(`${baseUrl}/warden/search-applicant`, {
                        params: { query: admissionNo }
                    });
                    setOptions(response.data.map(no => ({ value: no })));
                } catch (error) {
                    console.error('Error searching admission numbers:', error);
                }
            } else {
                setOptions([]);
            }
        };

        searchAdmissionNumbers();
    }, [admissionNo]);

    // Fetch student profile
    const fetchProfile = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${baseUrl}/warden/get-hostel-applicant-info`, {
                params: { admissionNo: admissionNo }
            });
            setProfile(res.data);
            form.setFieldsValue(res.data);
        } catch (error) {
            console.log('error', error)
            message.error(error.response.data.msg);
        } finally {
            setLoading(false);
        }
    };

    // Fetch HDF details
    const getHdfDetails = async () => {
        try {
            const res = await axios.get(`${baseUrl}/warden/get-individual-hdf`, {
                params: { admissionNo: admissionNo }
            });
            setHdfDetails(res.data);
            setPaymentAmount(res.data.value);
        } catch (error) {
            message.error('Failed to fetch HDF details');
        }
    };

    // Handle payment amount change
    const handlePaymentAmountChange = (value) => {
        const amount = parseFloat(value) || 0;
        if (amount > hdfDetails.value) {
            message.error('Payment amount cannot exceed remaining HDF amount');
            return;
        }
        setPaymentAmount(amount);
    };

    // Handle HDF payment
    const handleHdfPayment = async () => {
        try {
            const response = await axios.post(`${baseUrl}/clerk-a1/store-hdf-fee`, {
                admissionNo,
                totalAmount: paymentAmount,
                hdfDetails,
            });

            if (response.data) {
                await generateAndDownloadHostelFeeBill(
                    [{
                        fee_id: 13,
                        fee_name: 'Hostel Development Fee',
                        value: paymentAmount
                    }],
                    { ...profile, receipt_no: response.data.receipt_no }
                );

                setModalHeading("Success!");
                setModalText("HDF payment processed successfully");
                setOpen1(true);

                // Reset form
                setHdfDetails(null);
                setPaymentAmount(0);
                setProfile(null);
                setAdmissionNo('');
                form.resetFields();
            }
        } catch (error) {
            console.error('Error processing HDF payment:', error);
            setModalHeading("Error");
            setModalText("Failed to process HDF payment. Please try again.");
            setOpen1(true);
        }
    };

    // Render student details
    const renderStudentDetails = () => {
        const detailsToShow = [
            { label: 'Student Name', value: profile?.name },
            { label: 'Admission Number', value: profile?.admission_no },
            { label: 'Department', value: profile?.department },
            { label: 'Programme', value: profile?.programme },
            { label: 'Year of Admission', value: profile?.year_of_admission },
            { label: 'Current Year', value: profile?.current_year },
            { label: 'Admission Category', value: profile?.admission_category },
            { label: 'Gender', value: profile?.gender },
        ];

        return (
            <div className="grid grid-cols-2 gap-4 mb-6">
                {detailsToShow.map((detail, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-md">
                        <div className="text-xs text-gray-500 mb-1">{detail.label}</div>
                        <div className="font-semibold text-gray-800">{detail.value || 'N/A'}</div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <Card
                title="Hostel Development Fee (HDF) Payment"
                extra={
                    <div className="flex items-center space-x-4">
                        <AutoComplete
                            value={admissionNo}
                            options={options}
                            onSelect={setAdmissionNo}
                            onSearch={setAdmissionNo}
                            placeholder="Enter Admission No"
                            className="w-64"
                        />
                        <Button
                            type="secondary"
                            icon={<SearchOutlined />}
                            onClick={fetchProfile}
                            loading={loading}
                        >
                            View Profile
                        </Button>
                    </div>
                }
            >
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Spin size="large" />
                    </div>
                ) : profile ? (
                    <>
                        {renderStudentDetails()}

                        <Button
                            type="default"
                            icon={<FileSearchOutlined />}
                            onClick={getHdfDetails}
                            className="mb-4"
                        >
                            Fetch HDF Details
                        </Button>

                        {hdfDetails && (
                            <Card
                                title="HDF Payment Details"
                                className="mt-4"
                            >
                                <div className="space-y-4">
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-sm text-gray-600">Fee Name:</span>
                                                <div className="font-semibold">{hdfDetails.fee_name}</div>
                                            </div>
                                            <div>
                                                <span className="text-sm text-gray-600">Amount:</span>
                                                <div className="font-semibold text-red-600">Rs. {hdfDetails.value}/-</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Payment Amount
                                        </label>
                                        <Input
                                            type="number"
                                            value={paymentAmount}
                                            onChange={(e) => handlePaymentAmountChange(e.target.value)}
                                            placeholder="Enter amount to pay"
                                            className="w-full"
                                            min="0"
                                            max={hdfDetails.value}
                                        />
                                        <div className="text-sm text-gray-500">
                                            Maximum payable amount: Rs. {hdfDetails.value}/-
                                        </div>
                                    </div> */}

                                    <div className="flex justify-between items-center pt-4 border-t">
                                        <div>
                                            <strong className="text-lg">Payment Amount: Rs. {paymentAmount}/-</strong>
                                        </div>
                                        <button
                                            disabled={paymentAmount <= 0 || paymentAmount > hdfDetails.value}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setModalHeading("Confirm HDF Payment")
                                                setModalText(`Are you sure you want to pay Rs. ${paymentAmount}/- for HDF?`)
                                                setOpen2(true)
                                            }}
                                            className={
                                                paymentAmount > 0 && paymentAmount <= hdfDetails.value
                                                    ? "bg-stone-800 text-white px-6 py-3 rounded-lg text-sm hover:bg-stone-700"
                                                    : "bg-[#e0e0e0] text-[#a6a6a6] px-6 py-3 rounded-lg text-sm cursor-not-allowed"
                                            }
                                        >
                                            Pay HDF
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        )}
                    </>
                ) : (
                    <div className="text-center text-gray-500 py-10">
                        Enter an Admission Number to view student details
                    </div>
                )}
            </Card>

            <AlertDialog
                open={open1}
                setOpen={setOpen1}
                modalHeading={modalHeading}
                modalText={modalText}
            />
            <ConfirmDialog
                open={open2}
                setOpen={setOpen2}
                modalHeading={modalHeading}
                modalText={modalText}
                confirmFunction={() => handleHdfPayment()}
            />
        </div>
    );
};

export default PayHostelDevelopmentFee;